import type { RootState } from '@/store';
import { createSelector } from 'reselect';
import {
  defaultMatrixValue,
  deserialize,
  removeMatrixValue,
  serialize
} from './medicationDosageMatrixUtils';
import isEqual from 'lodash/fp/isEqual';
import { isNotEmpty } from '@/util';

export const selectTriageContext = (state: RootState) =>
  state.appContext.triage.context;

export const selectTriageContextGroup = (state: RootState) =>
  state.appContext.triage.context.group;

export const selectPatientDetailsEditable = (state: RootState) =>
  state.appContext.triage.editable;

const selectScratchConsultationEligibility = (state: RootState) =>
  state.appContext.triage.scratchConsultationEligibility;

export const selectIsScratchConsultationAvailable = createSelector(
  selectScratchConsultationEligibility,
  (elig) => Object.values(elig).every(Boolean)
);

export const selectIsProtocolDrawerOpen = (state: RootState) =>
  state.appContext.protocolDrawer.open;

export const selectProtocolDrawerState = (state: RootState) =>
  state.appContext.protocolDrawer;

export const selectProtocolDrawerData = (state: RootState) =>
  state.appContext.protocolDrawer.data;

export const selectCurrentHospital = (state: RootState) =>
  state.appContext.currentHospital;

export const selectScoresDrawerState = (state: RootState) =>
  state.appContext.medicalScoresDrawer;

export const selectScSuggestionDrawerState = (state: RootState) =>
  state.appContext.scSuggestionDrawer;

export const selectMedicationDrawerState = (state: RootState) =>
  state.appContext.medicationDrawer;

export const selectPremedicationDrawerState = (state: RootState) =>
  state.appContext.premedicationDrawer;

export const selectMedicationDrawerDosageMatrix = (state: RootState) =>
  state.appContext.medicationDrawer.dosageMatrix;

export const selectMedicationDrawerDosageMatrixSerialized = createSelector(
  selectMedicationDrawerDosageMatrix,
  serialize
);

export const selectIsMedicationDirty = createSelector(
  (state: RootState) => state.appContext.medicationDrawer.persistedMedication,
  (state: RootState) => state.appContext.medicationDrawer.medication,
  (state: RootState) => state.appContext.medicationDrawer.dosageMatrix,
  (persistedMed, currentMed, dosageMatrix) => {
    const { dosage: persistedDosage, ...persisted } = persistedMed ?? {};
    const { dosage: currentDosage, ...current } = currentMed ?? {};
    if (
      !persisted ||
      !isEqual(persisted, {
        ...current,
        bridgedBy: current?.bridgedBy?.filter(isNotEmpty)
      })
    ) {
      return true;
    }

    if (
      !isEqual(deserialize(persistedDosage) ?? defaultMatrixValue, dosageMatrix)
    ) {
      return true;
    }

    if (dosageMatrix) {
      const strippedDosage = removeMatrixValue(persistedDosage, dosageMatrix);
      if (!isEqual(strippedDosage, currentDosage)) {
        return true;
      }
    }

    return false;
  }
);

export const selectEpicDocument = (state: RootState) => {
  const epicDoc = state.appContext.epicDocumentsDrawer.epicDocument;
  if (typeof epicDoc === 'object') {
    return epicDoc;
  }

  return undefined;
};

export const selectEpicDocumentId = (state: RootState) => {
  const epicDoc = state.appContext.epicDocumentsDrawer.epicDocument;
  if (typeof epicDoc !== 'object') {
    return epicDoc;
  }

  return epicDoc?.id;
};

export const selectEpicExtPatientIdNewPatientFormValue = (state: RootState) =>
  state.appContext.newPatientDrawer.formValues.epicExtPatientId;

export const selectMedicationInstructionsDrawerState = (state: RootState) =>
  state.appContext.medicationInstructionsDrawer;

export const selectBillingPatientDrawerState = (state: RootState) =>
  state.appContext.billingPatientDrawer;
